import { useCallback, useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Plus, UserPlus, Send, Trash2, KeyRound, ShieldBan, ShieldCheck,
  MoreHorizontal, Pencil, Search, Lock, Copy, Check, MailCheck,
} from "lucide-react";
import {
  useUsers, useInviteUser, useUpdateUserRole, useDeleteUser,
  useResendInvite, useResetPassword, useUpdateProfile,
  useBanUser, useUnbanUser, useSetPassword, useConfirmEmail, useUsersLastActive, UserRow,
} from "@/hooks/useUsers";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
// #781: the same rank rule the manage-users function enforces (#773), so the UI
// can't drift from the server. The server stays the control; this is only UX.
import { canActOnTarget } from "../../supabase/functions/manage-users/role-guard.ts";
import { format } from "date-fns";

/* ── Invite Dialog ── */
function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("staff");
  const invite = useInviteUser();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await invite.mutateAsync({
        email: email.trim(),
        display_name: displayName.trim() || undefined,
        role,
        redirect_origin: window.location.origin,
      });
    } catch {
      return; // the mutation's onError already raised the toast; keep what they typed
    }
    onOpenChange(false);
    setEmail("");
    setDisplayName("");
    setRole("staff");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Invite Team Member</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="team@example.com" required />
          </div>
          <div className="space-y-2">
            <Label>Display Name</Label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Their name" />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={invite.isPending}>
              <UserPlus className="h-4 w-4 mr-2" />Send Invite
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Edit Profile Dialog ── */
function EditProfileDialog({
  user: u,
  open,
  onOpenChange,
}: { user: UserRow | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [displayName, setDisplayName] = useState(u?.display_name ?? "");
  const [phone, setPhone] = useState(u?.phone ?? "");
  const updateProfile = useUpdateProfile();

  // #1460: Radix never calls onOpenChange when the parent opens the dialog, so
  // seed the fields here or they open empty.
  useEffect(() => {
    if (open && u) {
      setDisplayName(u.display_name ?? "");
      setPhone(u.phone ?? "");
    }
  }, [open, u]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!u) return;
    // update-profile writes every field it is sent, so send only what changed.
    const updates: { display_name?: string; phone?: string | null } = {};
    const nextName = displayName.trim();
    const nextPhone = phone.trim() || null;
    if (nextName !== (u.display_name ?? "").trim()) updates.display_name = nextName;
    if (nextPhone !== (u.phone?.trim() || null)) updates.phone = nextPhone;
    if (Object.keys(updates).length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      await updateProfile.mutateAsync({ user_id: u.id, ...updates });
    } catch {
      return; // the mutation's onError already raised the toast
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Edit Profile</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Display Name</Label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Name" />
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555-000-0000" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={updateProfile.isPending}>Save Changes</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Set Password Dialog ── */
function generatePassword() {
  const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789!@#$%";
  let pw = "";
  for (let i = 0; i < 12; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  return pw;
}

function SetPasswordDialog({
  user: u,
  open,
  onOpenChange,
}: { user: UserRow | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const setPassword = useSetPassword();
  const [password, setPasswordValue] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = useCallback(() => {
    setPasswordValue(generatePassword());
    setCopied(false);
  }, []);

  // #1460: Radix doesn't call onOpenChange on a controlled open, so generate here.
  useEffect(() => {
    if (open) generate();
  }, [open, generate]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!u || !password.trim()) return;
    try {
      await setPassword.mutateAsync({ user_id: u.id, password: password.trim() });
    } catch {
      return; // the mutation's onError already raised the toast
    }
    onOpenChange(false);
    setPasswordValue("");
  };

  const handleOpen = (o: boolean) => {
    if (!o) { setPasswordValue(""); setCopied(false); }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Set Password — {u?.display_name || u?.email}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Generate a temporary password for this user. Share it with them securely — they can change it after logging in.
          </p>
          {!u?.email_confirmed_at && (
            <p className="text-sm text-muted-foreground">
              This also marks their email verified, so the password works straight away.
              Any invite link still sitting in their inbox stops working.
            </p>
          )}
          <div className="space-y-2">
            <Label>Password</Label>
            <div className="flex gap-2">
              <Input
                value={password}
                onChange={(e) => { setPasswordValue(e.target.value); setCopied(false); }}
                placeholder="Enter or generate password"
                className="font-mono"
              />
              <Button type="button" variant="outline" size="icon" onClick={handleCopy} disabled={!password} title="Copy">
                {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="flex justify-between">
            <Button type="button" variant="outline" onClick={generate}>
              <KeyRound className="h-4 w-4 mr-2" />Generate New
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => handleOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={setPassword.isPending || !password.trim()}>
                Set Password
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main Page ── */
const OUTRANKED_HINT = "Only a super admin can manage this account.";

function OutrankedTooltip({ outranked, children }: { outranked: boolean; children: React.ReactNode }) {
  if (!outranked) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Some browsers send a disabled button no pointer events, so the span takes them and carries the tooltip. */}
        <span tabIndex={0} className="inline-block [&_button]:pointer-events-none">{children}</span>
      </TooltipTrigger>
      <TooltipContent>{OUTRANKED_HINT}</TooltipContent>
    </Tooltip>
  );
}

const ROLE_VARIANT: Record<string, "destructive" | "default" | "secondary" | "outline"> = {
  super_admin: "destructive",
  admin: "destructive",
  staff: "default",
  viewer: "secondary",
};

function StatusBadge({ user: u }: { user: UserRow }) {
  if (u.banned) return <Badge variant="destructive" className="text-[10px]">Suspended</Badge>;
  if (!u.email_confirmed_at) {
    return (
      <Badge
        variant="outline"
        className="text-[10px]"
        title={'This address has not been verified yet. Until it is, sign-in fails with "Email not confirmed" even with the right password.'}
      >
        Pending
      </Badge>
    );
  }
  return <Badge variant="secondary" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Active</Badge>;
}

/**
 * #1183: the user's last in-app change. "—" while it loads or when the read
 * failed, so a failure never reads as "Never".
 */
function LastActive({
  userId,
  lastActive,
}: {
  userId: string;
  lastActive: { data?: Map<string, string>; isError: boolean };
}) {
  if (lastActive.isError) return <span title="Couldn't load activity">—</span>;
  if (!lastActive.data) return <>—</>;
  const at = lastActive.data.get(userId);
  return <>{at ? format(new Date(at), "MMM d, yyyy") : "Never"}</>;
}

export default function UsersPage() {
  const { data: users, isLoading, error: usersError } = useUsers();
  const lastActive = useUsersLastActive();
  const updateRole = useUpdateUserRole();
  const deleteUser = useDeleteUser();
  const resendInvite = useResendInvite();
  const resetPassword = useResetPassword();
  const banUser = useBanUser();
  const unbanUser = useUnbanUser();
  const confirmEmail = useConfirmEmail();
  const { user, roles } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserRow | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: string; user: UserRow } | null>(null);
  const [search, setSearch] = useState("");
  const [setPassUser, setSetPassUser] = useState<UserRow | null>(null);

  const filtered = users?.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      u.display_name?.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.roles.some((r) => r.includes(q)) ||
      // #719: role-less accounts are the ones an admin comes here to find, and
      // they match no role string. Let the badge text find them.
      (u.roles.length === 0 && "no role".includes(q))
    );
  });

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { type, user: u } = confirmAction;
    try {
      if (type === "delete") await deleteUser.mutateAsync({ user_id: u.id });
      if (type === "reset-password") await resetPassword.mutateAsync({ user_id: u.id, redirect_to: `${window.location.origin}/reset-password` });
      if (type === "ban") await banUser.mutateAsync({ user_id: u.id });
      if (type === "unban") await unbanUser.mutateAsync({ user_id: u.id });
      if (type === "confirm-email") await confirmEmail.mutateAsync({ user_id: u.id });
    } catch {
      // the mutation's onError already raised the toast
    } finally {
      setConfirmAction(null);
    }
  };

  const confirmMessages: Record<string, { title: string; desc: string; action: string; destructive?: boolean }> = {
    delete: {
      title: "Remove user",
      desc: "This will permanently delete this user and revoke their access. This action cannot be undone.",
      action: "Remove",
      destructive: true,
    },
    "reset-password": {
      title: "Send password reset",
      desc: "This will send a password reset email to the user. They will be able to set a new password.",
      action: "Send Reset Email",
    },
    ban: {
      title: "Suspend user",
      desc: "This will immediately revoke this user's access. They won't be able to log in until reactivated.",
      action: "Suspend",
      destructive: true,
    },
    unban: {
      title: "Reactivate user",
      desc: "This will restore this user's access. They will be able to log in again.",
      action: "Reactivate",
    },
    "confirm-email": {
      title: "Mark email verified",
      desc:
        "Marks this address verified without an email round trip — use it when the invite link has expired. " +
        "It does not create a password: if this person has never set one, follow up with Set Password or Reset Password. " +
        "Any invite link still sitting in their inbox stops working.",
      action: "Mark Verified",
    },
  };

  const cm = confirmAction ? confirmMessages[confirmAction.type] : null;

  return (
    <DashboardLayout title="Team">
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button className="shrink-0" onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />Invite Member
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">Loading…</div>
        ) : usersError ? (
          <div className="text-center py-12 text-sm text-destructive">
            Couldn't load team members: {usersError.message}
          </div>
        ) : !filtered?.length ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-4 mb-4"><Plus className="h-8 w-8 text-muted-foreground" /></div>
              <h3 className="font-display text-lg font-semibold mb-1">
                {search ? "No matching members" : "No team members"}
              </h3>
              <p className="text-muted-foreground text-sm">
                {search ? "Try a different search term." : "Invite your first team member to get started."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Sign In</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => {
                  const isSelf = u.id === user?.id;
                  // Every per-user action 403s when the row outranks the caller.
                  const canManage = canActOnTarget(roles, u.roles);
                  // #719: accounts start with no role now, so "no role" is a real
                  // state and must not render as a viewer.
                  const primaryRole = u.roles[0] ?? null;
                  return (
                    <TableRow key={u.id} className={u.banned ? "opacity-60" : ""}>
                      <TableCell className="font-medium">{u.display_name || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        {isSelf ? (
                          <Badge variant={primaryRole ? ROLE_VARIANT[primaryRole] ?? "outline" : "outline"}>
                            {primaryRole ?? "no role"}
                          </Badge>
                        ) : (
                          <OutrankedTooltip outranked={!canManage}>
                            <Select
                              value={primaryRole ?? ""}
                              disabled={!canManage}
                              onValueChange={(role) => updateRole.mutate({ user_id: u.id, role })}
                            >
                              <SelectTrigger className="w-[100px] h-7 text-xs">
                                {/* Children, not a matching item, so a super_admin row still shows its role. */}
                                <SelectValue placeholder="no role">{primaryRole}</SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">admin</SelectItem>
                                <SelectItem value="staff">staff</SelectItem>
                                <SelectItem value="viewer">viewer</SelectItem>
                              </SelectContent>
                            </Select>
                          </OutrankedTooltip>
                        )}
                      </TableCell>
                      <TableCell><StatusBadge user={u} /></TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {u.last_sign_in_at ? format(new Date(u.last_sign_in_at), "MMM d, yyyy") : "Never"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <LastActive userId={u.id} lastActive={lastActive} />
                      </TableCell>
                      <TableCell>
                        {!isSelf && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {!canManage && (
                                <>
                                  <DropdownMenuLabel className="max-w-[14rem] text-xs font-normal text-muted-foreground">
                                    {OUTRANKED_HINT}
                                  </DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              {/* onSelect, not onClick: Radix still runs onClick on a disabled item. */}
                              <DropdownMenuItem disabled={!canManage} onSelect={() => setEditUser(u)}>
                                <Pencil className="h-4 w-4 mr-2" />Edit Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={!canManage}
                                onSelect={() =>
                                  resendInvite.mutate({
                                    user_id: u.id,
                                    redirect_origin: window.location.origin,
                                  })
                                }
                              >
                                <Send className="h-4 w-4 mr-2" />
                                {u.email_confirmed_at ? "Resend Invite" : "Resend verification email"}
                              </DropdownMenuItem>
                              {!u.email_confirmed_at && (
                                <DropdownMenuItem
                                  disabled={!canManage}
                                  onSelect={() => setConfirmAction({ type: "confirm-email", user: u })}
                                >
                                  <MailCheck className="h-4 w-4 mr-2" />Mark email verified
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                disabled={!canManage}
                                onSelect={() => setConfirmAction({ type: "reset-password", user: u })}
                              >
                                <KeyRound className="h-4 w-4 mr-2" />Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled={!canManage} onSelect={() => setSetPassUser(u)}>
                                <Lock className="h-4 w-4 mr-2" />Set Password
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {u.banned ? (
                                <DropdownMenuItem
                                  disabled={!canManage}
                                  onSelect={() => setConfirmAction({ type: "unban", user: u })}
                                >
                                  <ShieldCheck className="h-4 w-4 mr-2" />Reactivate
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  disabled={!canManage}
                                  onSelect={() => setConfirmAction({ type: "ban", user: u })}
                                >
                                  <ShieldBan className="h-4 w-4 mr-2" />Suspend
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                disabled={!canManage}
                                onSelect={() => setConfirmAction({ type: "delete", user: u })}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <EditProfileDialog user={editUser} open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)} />
      <SetPasswordDialog user={setPassUser} open={!!setPassUser} onOpenChange={(o) => !o && setSetPassUser(null)} />

      {/* Confirm Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{cm?.title} — {confirmAction?.user.display_name || confirmAction?.user.email}</AlertDialogTitle>
            <AlertDialogDescription>{cm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cm?.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
              onClick={handleConfirm}
            >
              {cm?.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
